import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import {
  ControlContainer,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NotificationService } from '@core/services/notification.service';
import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { EquipmentService } from '@sales/services/equipment.service';

import {
  CustomerEquipmentUpgradeAgreement,
  MobileReturnMethod,
} from '@sales/modules/mobilephone/models/equipment-upgrade-agreement.model';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppData } from '@shared/models/appdata.model';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { RadioButtonListItemComponent } from '@app/shared/components/atoms/radio-button-list/radio-button-list-item/radio-button-list-item.component';
import { RadioButtonListComponent } from '@app/shared/components/atoms/radio-button-list/radio-button-list.component';
import { MatRadioGroup } from '@angular/material/radio';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';

enum Messages {
  CreateInsuranceError = 'Feil ved opprettelse av forsikring, prøv igjen senere',
}

@Component({
  selector: 'app-return-mobile-phone-details',
  templateUrl: './return-mobile-phone-details.component.html',
  styleUrls: ['./return-mobile-phone-details.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceCardComponent,
    FormsModule,
    ReactiveFormsModule,
    MatRadioGroup,
    RadioButtonListComponent,
    RadioButtonListItemComponent,
    MatCheckbox,
    IceButtonComponent,
  ],
})
export class ReturnMobilePhoneDetailsComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  private formBuilder = inject(FormBuilder);
  private equipmentService = inject(EquipmentService);
  private notificationService = inject(NotificationService);
  private appData = inject(AppData);

  MobileReturnMethod = MobileReturnMethod;

  @Input() numberOfSteps: number;
  @Input() equipmentUpgradeAgreement: CustomerEquipmentUpgradeAgreement;
  @Input() canSellHardwareWithLogistics: boolean;
  @Input() redemptionId: number;
  @Output() navigateToNextStep = new EventEmitter();
  @Output() setRedemptionId = new EventEmitter();

  form: FormGroup;
  subscriptions$: Subscription[] = [];
  matcher = new ErrorStateOnTouched();
  isInitializingRedemption: boolean;
  isReturnPhoneInStoreEnabled = this.appData.isReturnPhoneInStoreEnabled;

  ngOnInit() {
    if (!this.formGroupDirective.form.controls.returnGroup) {
      this.formGroupDirective.form.addControl(
        'returnGroup',
        this.formBuilder.group({
          returnMethodControl: this.formBuilder.control(
            this.canSellHardwareWithLogistics
              ? MobileReturnMethod.ReturnPackage
              : this.isReturnPhoneInStoreEnabled === 'true'
                ? MobileReturnMethod.ShopReturn
                : MobileReturnMethod.ReturnPackage,
            Validators.required
          ),
          mobileOwnership: this.formBuilder.control(false, Validators.requiredTrue),
        })
      );
    }

    this.form = this.formGroupDirective.form.controls.returnGroup as FormGroup;

    this.subscriptions$.push(
      this.form.controls.returnMethodControl.valueChanges.subscribe(() => this.setRedemptionId.emit(null))
    );
  }

  onNavigateToNextStep() {
    if (this.redemptionId) {
      this.navigateToNextStep.emit();
    }
    this.isInitializingRedemption = true;
    this.subscriptions$.push(
      this.equipmentService
        .initializeRedemption(
          this.equipmentUpgradeAgreement.agreementId,
          this.form.controls.returnMethodControl?.value === MobileReturnMethod.ShopReturn
        )
        .pipe(
          tap(
            redemptionIdResult => {
              this.isInitializingRedemption = false;
              this.setRedemptionId.emit(redemptionIdResult);
              this.navigateToNextStep.emit();
            },
            () => {
              this.isInitializingRedemption = false;
              this.notificationService.error(Messages.CreateInsuranceError);
            }
          )
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
